import React, { useState, useEffect } from 'react';
import axios from 'axios';
import apiUrl from '../../Apiurl';
import stilePulsanti from '../../interfaccia/stilePulsanti';
import SettingBooleano from '../../statistiche/SettingBooleano';
import { LineChart } from "@mui/x-charts/LineChart";
import RicercaAvanzataOrdiniPOP from '../AreaOrdiniComponenti/DBperAreaOrdiniComponenti/RicercaAvanzataOrdiniPOP';
import { formatDashboardOrdiniFilters } from "./dashboardOrdiniMeta";
import { LineChartSkeleton } from "../../interfaccia/loading";

function StatTrendConfronta({
    mobile,
    token,
    type, // 'ordini' | 'articoli'
    campiRicerca, // array of fields for the modal
    modalTitle 
}) {
    const {
        buttonClassesWhite,
        buttonClassesGreen,
        buttonClassesOrange,
        inputClassesRicercaOrizzontale,
        tastoPiuAspetto,
    } = stilePulsanti;

    const [years, setYears] = useState(3);
    const [metric, setMetric] = useState("importo"); // "importo" | "quantita"
    const [stack, setStack] = useState(false);
    const [channel, setChannel] = useState("Tutti"); // Tutti, Amazon, PrestaShop
    
    // Series Management
    const [seriesList, setSeriesList] = useState([]); // [{id, label, filters, color, visible}]
    const [chartData, setChartData] = useState([]);
    const [loading, setLoading] = useState(false);
    
    // Modal State
    const [showModal, setShowModal] = useState(false);
    const [tempFilters, setTempFilters] = useState({}); // To capture filters from modal

    // Palette
    const PALETTE = [
        "#1f77b4", "#ff7f0e", "#2ca02c", "#d62728", "#9467bd", 
        "#8c564b", "#e377c2", "#7f7f7f", "#bcbd22", "#17becf"
    ];

    // Helper to get next color
    const getNextColor = () => PALETTE[seriesList.length % PALETTE.length];

    const generateLabel = (filters) =>
        formatDashboardOrdiniFilters(filters, campiRicerca);

    const [capturedFilters, setCapturedFilters] = useState(null);

    useEffect(() => {
        if (capturedFilters) {
            // User submitted search
            addSeries(capturedFilters);
            setCapturedFilters(null);
            setShowModal(false);
        }
    }, [capturedFilters]);

    const addSeries = (filters) => {
        const newSeries = {
            id: Date.now(),
            label: generateLabel(filters),
            filters: filters,
            color: getNextColor(),
            visible: true
        };
        setSeriesList([...seriesList, newSeries]);
    };

    const removeSeries = (id) => {
        setSeriesList(seriesList.filter(s => s.id !== id));
    };

    const toggleSeries = (id) => {
        setSeriesList(seriesList.map(s => s.id === id ? { ...s, visible: !s.visible } : s));
    };
    
    // Data Fetching
    useEffect(() => {
        const fetchAll = async () => {
             if (!token || seriesList.length === 0) {
                 setChartData([]);
                 return;
             }
             
             setLoading(true);
             try {
                 // Fetch all series in parallel
                 const promises = seriesList.map(s => {
                     const params = new URLSearchParams({
                         type: type,
                         filters: JSON.stringify(s.filters),
                         anni: String(years),
                         metric: metric,
                         channel: channel
                     });
                     return axios.get(`${apiUrl}/contab/charts/linesTrendCustom?${params.toString()}`, {
                         headers: { Authorization: `Bearer ${token}` }
                     });
                 });

                 const results = await Promise.all(promises);
                 
                 // Merge Data
                 // We need a map of Month -> { month: Date, seriesId1: val, seriesId2: val ... }
                 const dataMap = new Map();
                 
                 results.forEach((res, idx) => {
                     const series = seriesList[idx];
                     if(res.data && res.data.dataset) {
                         res.data.dataset.forEach(row => {
                             const m = row.month; // YYYY-MM
                             if (!dataMap.has(m)) {
                                 dataMap.set(m, { month: new Date(m + "-01") });
                             }
                             const entry = dataMap.get(m);
                             entry[series.id] = Number(row.value);
                         });
                     }
                 });
                 
                 // Fill missing series with null to avoid NaN in chart
                 const allSeriesIds = seriesList.map(s => s.id);
                 dataMap.forEach(entry => {
                     allSeriesIds.forEach(id => {
                         if (!(id in entry)) {
                             entry[id] = null;
                         }
                     });
                 });
                 
                 const merged = Array.from(dataMap.values()).sort((a,b) => a.month - b.month);
                 setChartData(merged);

             } catch (err) {
                 console.error("Error fetching custom trends", err);
             } finally {
                 setLoading(false);
             }
        };
        fetchAll();
    }, [seriesList, years, metric, channel, token]); // Re-fetch when series or global params change


    return (
        <div className="sfondoBiancoTema pa3 mb3">
            <div className="tc b mb2 f4">{metric === 'importo' ? 'Trend Importo' : 'Trend Quantità'} - {type}</div>
            
             {/* Controls */}
             <div className="flex flex-wrap items-center justify-between mb2">
                 <div className="mr3 flex items-center mb2">
                    <span className="mr2">ultimi</span>
                    <input type="number" min={1} max={10} value={years} onChange={e => setYears(e.target.value)} className={inputClassesRicercaOrizzontale + " w3 tc"} />
                    <span className="ml2">anni</span>
                </div>
                 <div className="mr3 flex items-center mb2">
                    <span className="mr2">imp/qta</span>
                    <SettingBooleano value={metric} trueElement="importo" falseElement="quantità" setTrue={() => setMetric("importo")} setFalse={() => setMetric("quantita")} customValueTrue="importo" customValueFalse="quantita" trueButtonClass={buttonClassesOrange} falseButtonClass={buttonClassesGreen} />
                </div>
                <div className="mr3 flex items-center mb2">
                    <span className="mr2">stack</span>
                    <SettingBooleano value={stack} trueElement="stack" falseElement="linee" setTrue={() => setStack(true)} setFalse={() => setStack(false)} trueButtonClass={buttonClassesOrange} falseButtonClass={buttonClassesGreen} />
                </div>
                <div className="mr3 flex items-center mb2">
                    <span className="mr2">Canale:</span>
                    <button onClick={() => setChannel("Tutti")} className={channel === 'Tutti' ? buttonClassesGreen : buttonClassesWhite}>Tutti</button>
                    <button onClick={() => setChannel("Amazon")} className={channel === 'Amazon' ? buttonClassesGreen : buttonClassesWhite}>Amz</button>
                    <button onClick={() => setChannel("PrestaShop")} className={channel === 'PrestaShop' ? buttonClassesGreen : buttonClassesWhite}>PS</button>
                </div>
             </div>

             {/* Series Tags & Add Button */}
             <div className="flex flex-wrap items-center mb3">
                 {seriesList.map(s => (
                     <div key={s.id} className="ba b--black-10 br3 pa2 ma1 flex items-center" style={{ borderLeft: `5px solid ${s.color}`}}>
                         <div className="mr2 pointer" onClick={() => toggleSeries(s.id)} style={{ opacity: s.visible ? 1 : 0.5 }}>
                             <span className="b">{s.label}</span>
                         </div>
                         <div className="pointer red f7 ml2" onClick={() => removeSeries(s.id)}>X</div>
                     </div>
                 ))}
                 
                 <button className="dim tastoInputOrizzontale br2 f3 flex items-center justify-center pa2" onClick={() => setShowModal(true)}>
                     +
                 </button>
             </div>

             {/* Chart */}
             <div style={{ width: '100%', height: mobile ? 300 : 500 }}>
                 {loading ? (
                     <LineChartSkeleton
                        tone="soft"
                        height={mobile ? 300 : 500}
                        seriesCount={Math.max(2, Math.min(seriesList.length || 3, 4))}
                     />
                 ) : seriesList.length > 0 && chartData.length > 0 ? (
                     <LineChart
                        dataset={chartData}
                        xAxis={[{ dataKey: 'month', scaleType: 'time', valueFormatter: (v) => v ? new Date(v).toLocaleDateString("it-IT", {month:'2-digit', year:'2-digit'}) : "" }]}
                        series={seriesList.filter(s => s.visible).map(s => ({
                            dataKey: s.id,
                            label: s.label,
                            color: s.color,
                            valueFormatter: (v) => metric === "importo" ? `${Number(v).toLocaleString('it-IT')} €` : v,
                            ...(stack ? { stack: "total", area: true } : {})
                        }))}
                        height={mobile ? 300 : 500}
                        margin={{ left: 70, right: 20, top: 20, bottom: 30 }}
                     />
                 ) : (
                     <div className="tc gray pa4">Aggiungi una serie per visualizzare il grafico</div>
                 )}
             </div>

             {/* Modal */}
             {showModal && (
                 <div className="fixed top-0 left-0 w-100 h-100 flex items-center justify-center bg-black-60 z-max" onClick={() => setShowModal(false)} style={{zIndex: 9999}}>
                     <div className="bg-white pa3 br3 overflow-y-auto" style={{ maxHeight: '90vh', width: '90%' }} onClick={e => e.stopPropagation()}>
                         <RicercaAvanzataOrdiniPOP
                            setPopUpWindow={setShowModal}
                            campiRicerca={campiRicerca} // pass from prop
                            // Intercepting the search
                            setRicerca={setCapturedFilters}
                            soloFiltri={true}
                         />
                     </div>
                 </div>
             )}
        </div>
    );
}

export default StatTrendConfronta;
